import './defines';
import SvgIconComponent from 'components/svg-icon';
import {
	Model,
	Public,
} from 'utils/decorators';
import {
	Component,
	toNative,
} from 'utils/vue-facing-decorator';
import {
	Prop,
	Ref,
	Vue,
	Watch,
} from 'vue-facing-decorator';
import Template from './template.vue';

@Component({
	name: 'InputSliderComponent',
	components: {
		SvgIconComponent,
	},
	emits: [
		'change',
		'mousepress',
		'slider-mousedown',
		'slider-touchstart',
	],
	mixins: [Template],
})
class InputSliderComponent extends Vue {
	@Model({
		description: 'Defines the value of the slider',
		type: Number,
	})
	public readonly value!: number;

	@Prop({
		default: true,
		description: 'Indicates if the slider input value is editable or just informative',
		type: Boolean,
	})
	public readonly editable!: boolean;

	@Prop({
		default: 8,
		description: 'Defines the minimum value for the slider',
		type: Number,
	})
	public readonly min!: number;

	@Prop({
		default: 300,
		description: 'Defines the maximum value for the slider',
		type: Number,
	})
	public readonly max!: number;

	@Prop({
		default: false,
		description: 'Indicates if the increase/decrease buttons should be shown',
		type: Boolean,
	})
	public readonly showIncreaseDecreaseButtons!: boolean;

	@Prop({
		default: false,
		description: 'Indicates if the slider should show the steps dots',
		type: Boolean,
	})
	public readonly showSteps!: boolean;

	@Prop({
		default: true,
		description: 'Indicates if the value and input (for manual inputting) should be shown',
		type: Boolean,
	})
	public readonly showValue!: boolean;

	@Prop({
		default: '--primary1',
		description: 'Defines the color of the slider thumb',
		type: String,
	})
	public readonly thumbColor!: string;

	@Prop({
		default: 16,
		description: 'Defines the size of the slider thumb icon',
		type: Number,
	})
	public readonly thumbIconSize!: number;

	@Prop({
		default: 24,
		description: 'Defines the size of the slider handle',
		type: Number,
	})
	public readonly handleSize?: number;

	@Prop({
		default: undefined,
		description: 'Defines the color of the slider track',
		type: String,
	})
	public readonly trackColor?: string;

	@Prop({
		default: undefined,
		description: 'Defines the color of the slider step dots',
		type: String,
	})
	public readonly stepColor?: string;

	@Prop({
		default: undefined,
		description: 'Defines the color of the slider handle icon',
		type: String,
	})
	public readonly handleIconColor?: string;

	@Prop({
		default: true,
		description: 'Indicates if the slider handle should show an icon',
		type: Boolean,
	})
	public readonly showThumbIcon!: boolean;

	@Prop({
		default: 24,
		type: Number,
	})
	public readonly trackHeight!: number | string;

	@Prop({
		default: 200,
		description: 'Defines the width of the slider track',
		type: Number,
	})
	public readonly trackWidth!: number;

	protected get sliderStyle(): Partial<CSSStyleDeclaration> & Record<string, string> {
		const styles: Partial<CSSStyleDeclaration> & Record<string, string> = {};
		styles.width = `${this.trackWidth}px`;
		styles['--track-height'] = `${this.trackHeight}px`;

		if (this.handleSize) {
			styles['--slider-handle-size'] = `${this.handleSize}px`;
		}

		if (this.trackColor) {
			styles['--input-slider-track-color'] = this.trackColor.startsWith('--')
				? `var(${this.trackColor})`
				: this.trackColor;
		}

		if (this.stepColor) {
			styles['--input-slider-step-color'] = this.stepColor.startsWith('--')
				? `var(${this.stepColor})`
				: this.stepColor;
		}

		if (this.handleIconColor) {
			styles['--input-slider-handle-icon-color'] = this.handleIconColor.startsWith('--')
				? `var(${this.handleIconColor})`
				: this.handleIconColor;
		}

		return styles;
	}

	protected get sliderHandleStyle(): Partial<CSSStyleDeclaration> {
		const styles: Partial<CSSStyleDeclaration> = {};
		styles.transform = `translateX(${this.sliderHandleTranslate}px)`;

		if (
			this.inputSliderElement
			&& this.inputSliderHandleElement
		) {
			const translateY = ((this.inputSliderHandleElement.offsetHeight / 2) - (this.inputSliderElement.offsetHeight / 2));

			if (translateY) {
				styles.transform = `${styles.transform} translateY(-${translateY}px)`;
			}
		}
		if (
			this.thumbColor.substring(
				0,
				2,
			) === '--'
		) {
			styles.backgroundColor = `var(${this.thumbColor})`;
		} else {
			styles.backgroundColor = this.thumbColor;
		}

		return styles;
	}

	protected get sliderHandleTranslate(): number {
		const translate = this.trackInnerWidth * (this.internalValuePercentage / 100);

		if (translate <= 0) {
			return 0;
		}
		if (translate >= this.trackInnerWidth) {
			return this.trackInnerWidth;
		}

		return translate;
	}

	private get internalValuePercentage(): number {
		return (this.internalValue - this.min) / (this.max - this.min) * 100;
	}

	@Ref('inputSlider')
	private readonly inputSliderElement!: HTMLElement;

	@Ref('inputSliderHandle')
	private readonly inputSliderHandleElement!: HTMLElement;

	@Ref('inputSliderTrack')
	private readonly inputSliderTrackElement?: HTMLElement;

	private internalValue = 0;

	private isMouseDown?: boolean;

	private mouseDownInterval?: NodeJS.Timeout;

	private mouseDownTimeout?: NodeJS.Timeout;

	private resizeObserver?: ResizeObserver;

	private trackInnerWidth = 0;

	protected beforeUnmount(): void {
		window.removeEventListener(
			'mouseup',
			this.onWindowMouseUp,
		);
		window.removeEventListener(
			'touchend',
			this.onWindowMouseUp,
		);

		if (this.mouseDownInterval) {
			clearInterval(this.mouseDownInterval);
			this.mouseDownInterval = undefined;
		}

		if (this.mouseDownTimeout) {
			clearTimeout(this.mouseDownTimeout);
			this.mouseDownTimeout = undefined;
		}

		this.resizeObserver?.disconnect();
	}

	protected mounted(): void {
		this.$nextTick(() => {
			if (this.inputSliderTrackElement) {
				this.trackInnerWidth = this.inputSliderTrackElement.offsetWidth;
			}
		});
		this.resizeObserver = new ResizeObserver(this.onTrackWidthChange);
		this.resizeObserver.observe(this.inputSliderElement);
		this.$forceCompute('sliderHandleStyle');
	}

	@Watch('trackHeight')
	protected onTrackHeightChange(): void {
		requestAnimationFrame(() => {
			this.$forceCompute('sliderHandleStyle');
		});
	}

	@Watch('trackWidth')
	protected onTrackWidthChange(): void {
		requestAnimationFrame(() => {
			if (this.inputSliderTrackElement) {
				this.trackInnerWidth = this.inputSliderTrackElement.offsetWidth;
			}
		});
	}

	@Watch(
		'value',
		{
			immediate: true,
		},
	)
	protected onValueChange() {
		this.internalValue = this.value ?? this.min;
	}

	private decreaseValue(): void {
		const newValue = this.internalValue - 1;

		if (
			newValue >= this.min
			&& newValue !== this.internalValue
		) {
			this.internalValue = newValue;
			this.triggerInputEvent();
			this.triggerChangeEvent();
		}
	}

	/**
	 * Provides the handle element for tooltip anchoring.
	 *
	 * @returns The slider handle element, if available.
	 */
	@Public()
	public handleElement(): Decorated<HTMLElement> | undefined {
		return this.inputSliderHandleElement as Decorated<HTMLElement> | undefined;
	}

	private increaseValue(): void {
		const newValue = this.internalValue + 1;

		if (
			newValue <= this.max
			&& newValue !== this.internalValue
		) {
			this.internalValue = newValue;
			this.triggerInputEvent();
			this.triggerChangeEvent();
		}
	}

	protected onDecreaseClick(): void {
		this.decreaseValue();
	}

	protected onDecreaseMouseDown(): void {
		this.isMouseDown = true;
		window.addEventListener(
			'mouseup',
			this.onWindowMouseUp,
		);
		window.addEventListener(
			'touchend',
			this.onWindowMouseUp,
		);

		if (this.mouseDownTimeout) {
			clearTimeout(this.mouseDownTimeout);
		}

		this.mouseDownTimeout = setTimeout(
			() => {
				if (this.mouseDownInterval) {
					clearInterval(this.mouseDownInterval);
				}

				this.mouseDownInterval = setInterval(
					() => {
						if (!this.isMouseDown) {
							clearInterval(this.mouseDownInterval);
							this.mouseDownInterval = undefined;
							return;
						}

						this.$emit('mousepress');
						const newValue = this.internalValue - 1;

						if (
							newValue >= this.min
							&& newValue !== this.internalValue
						) {
							this.internalValue = newValue;
							this.triggerInputEvent();
							this.triggerChangeEvent();
						}
					},
					100,
				);
			},
			500,
		);
	}

	protected onIncreaseClick(): void {
		this.increaseValue();
	}

	protected onIncreaseMouseDown(): void {
		this.isMouseDown = true;
		window.addEventListener(
			'mouseup',
			this.onWindowMouseUp,
		);
		window.addEventListener(
			'touchend',
			this.onWindowMouseUp,
		);

		if (this.mouseDownTimeout) {
			clearTimeout(this.mouseDownTimeout);
		}

		this.mouseDownTimeout = setTimeout(
			() => {
				if (this.mouseDownInterval) {
					clearInterval(this.mouseDownInterval);
				}

				this.mouseDownInterval = setInterval(
					() => {
						if (!this.isMouseDown) {
							clearInterval(this.mouseDownInterval);
							this.mouseDownInterval = undefined;
							return;
						}

						this.$emit('mousepress');
						const newValue = this.internalValue + 1;

						if (
							newValue <= this.max
							&& newValue !== this.internalValue
						) {
							this.internalValue = newValue;
							this.triggerInputEvent();
							this.triggerChangeEvent();
						}
					},
					100,
				);
			},
			500,
		);
	}

	protected onInputChange(event: InputEvent): void {
		event.stopPropagation();
		const value = parseInt(
			(event.target as HTMLInputElement).value,
			10,
		) || 0;

		if (value < this.min) {
			/**
			 * If the value is lower than the min value, set the value to the min value
			 * We are using the trick of setting the internal value to `this.min - 1` before setting it to the min value
			 * to force the component to update the value of the input
			 */
			this.internalValue = this.min - 1;
			this.internalValue = this.min;
		} else if (value > this.max) {
			/**
			 * If the value is greater than the max value, set the value to the max value
			 * We are using the trick of setting the internal value to `this.max - 1` before setting it to the max value
			 * to force the component to update the value of the input
			 */
			this.internalValue = this.max - 1;
			this.internalValue = this.max;
		}

		this.triggerChangeEvent();
	}

	protected onInputInput(event: InputEvent): void {
		const value = parseInt(
			(event.target as HTMLInputElement).value,
			10,
		);

		if (
			value >= this.min
			&& value <= this.max
		) {
			this.internalValue = value;
		} else if (value > this.max) {
			/**
			 * If the value is greater than the max value, set the value to the max value
			 * We are using the trick of setting the internal value to `this.max - 1` before setting it to the max value
			 * to force the component to update the value of the input
			 */
			this.internalValue = this.max - 1;
			this.internalValue = this.max;
		}

		this.triggerInputEvent();
	}

	protected onSliderChange(event: InputEvent) {
		event.stopPropagation();
		this.triggerChangeEvent();
	}

	protected onSliderInput(event: InputEvent) {
		this.internalValue = parseInt(
			(event.target as HTMLInputElement).value,
			10,
		);
		this.triggerInputEvent();
	}

	protected onSliderMouseDown(event: MouseEvent): void {
		this.$emit(
			'slider-mousedown',
			event,
		);
	}

	protected onSliderTouchMove(event: TouchEvent) {
		event.stopPropagation();
	}

	protected onSliderTouchStart(event: TouchEvent): void {
		this.$emit(
			'slider-touchstart',
			event,
		);
	}

	protected onVisibilityChange(isVisible: boolean): void {
		if (isVisible) {
			this.$forceCompute('sliderHandleStyle');
		}
	}

	private onWindowMouseUp(): void {
		window.removeEventListener(
			'mouseup',
			this.onWindowMouseUp,
		);
		window.removeEventListener(
			'touchend',
			this.onWindowMouseUp,
		);
		this.isMouseDown = false;

		if (this.mouseDownInterval) {
			clearInterval(this.mouseDownInterval);
			this.mouseDownInterval = undefined;
		}

		if (this.mouseDownTimeout) {
			clearTimeout(this.mouseDownTimeout);
			this.mouseDownTimeout = undefined;
		}
	}

	private triggerChangeEvent(): void {
		this.$emit(
			'change',
			this.internalValue,
		);
	}

	private triggerInputEvent(): void {
		this.$emit(
			'update:value',
			this.internalValue,
		);
	}
}

export default toNative(InputSliderComponent);
